import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type ListCurrenciesResult } from '@whinerp/client';
import {
  Button,
  CapabilityUnavailable,
  ErrorState,
  Field,
  Num,
  Panel,
  SectionLabel,
  Skeleton,
} from '@whinerp/ui';
import { useErrorMessage, useT } from '../../app/i18n.js';
import { useEntitlement } from '../../app/session.js';

/**
 * Setup > Currencies and rates (F-023).
 *
 * Rates are entered and dated rather than fetched. The list is ordered newest
 * first per pair, because the question somebody has when they open this is
 * "what is it now" and the second question is "what was it in March" - both
 * answered by reading down.
 */
export function CurrencyPanel(): JSX.Element {
  const t = useT();
  const errorMessage = useErrorMessage();
  const queryClient = useQueryClient();
  const entitlement = useEntitlement('F-023');
  const [code, setCode] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [on, setOn] = useState('');
  const [rate, setRate] = useState('');

  const data = useQuery<ListCurrenciesResult, ApiError>({
    queryKey: ['currencies'],
    queryFn: () => api.listCurrencies({}),
    enabled: entitlement.state === 'on',
  });

  const addCurrency = useMutation({
    mutationFn: () => api.addCurrency({ body: { code } }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['currencies'] });
      setCode('');
    },
  });

  const saveRate = useMutation({
    mutationFn: () =>
      api.setExchangeRate({ body: { fromCurrency: from, toCurrency: to, effectiveOn: on, rate } }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['currencies'] });
      setRate('');
    },
  });

  if (entitlement.state === 'off' || entitlement.state === 'plan-locked') {
    return (
      <Panel data-region="currencies" id="currencies">
        <div className="flex flex-col gap-3">
          <SectionLabel>{t('fx.title')}</SectionLabel>
          <CapabilityUnavailable
            kind={entitlement.state === 'off' ? 'module-off' : 'plan-locked'}
            capability={t('fx.title')}
            title={t('capability.moduleOff.title')}
            body={t('capability.moduleOff.body', { values: { capability: t('fx.title') } })}
            action={{ label: t('capability.moduleOff.action'), href: '/setup/modules' }}
          />
        </div>
      </Panel>
    );
  }
  if (data.isPending) return <Skeleton className="h-block-md w-full" />;
  if (data.isError) {
    return <ErrorState title={t('state.error')} body={data.error.message} onRetry={() => void data.refetch()} />;
  }

  return (
    <Panel data-region="currencies" id="currencies">
      <div className="flex flex-col gap-3">
        <SectionLabel>{t('fx.title')}</SectionLabel>
        <p className="text-meta text-ink-meta">{t('fx.body')}</p>
        <p data-reporting-currency className="text-dense text-ink-600">
          {t('fx.reporting', { values: { code: data.data.reporting } })}
        </p>

        <ul className="flex flex-wrap gap-2">
          {data.data.currencies.map((currency) => (
            <li
              key={currency}
              data-currency={currency}
              className="rounded-chip bg-sunken px-2 py-0.5 font-mono text-meta text-ink-600"
            >
              {currency}
            </li>
          ))}
        </ul>

        <div className="flex flex-wrap items-end gap-2">
          <Field
            label={t('fx.code')}
            data-currency-code
            value={code}
            onChange={(event) => setCode(event.currentTarget.value.toUpperCase())}
          />
          <Button
            intent="secondary"
            size="small"
            data-add-currency
            busy={addCurrency.isPending}
            disabled={code.trim().length !== 3}
            onClick={() => addCurrency.mutate()}
          >
            {t('fx.add')}
          </Button>
        </div>
        {addCurrency.isError ? (
          <p data-currency-refusal className="text-dense text-danger-text">
            {errorMessage(addCurrency.error)}
          </p>
        ) : null}

        {/* Rates, newest first per pair. */}
        {data.data.rates.length === 0 ? (
          <p className="text-dense text-ink-meta">{t('fx.rate.none')}</p>
        ) : (
          <div data-scroll-container className="overflow-x-auto">
            <table className="w-full text-dense">
              <thead>
                <tr>
                  {['fx.rate.from', 'fx.rate.to', 'fx.rate.on', 'fx.rate.value'].map((key) => (
                    <th key={key} scope="col" className="p-1 text-start text-micro uppercase tracking-label text-ink-label">
                      {t(key as never)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.data.rates.map((row) => (
                  <tr
                    key={`${row.fromCurrency}-${row.toCurrency}-${row.effectiveOn}`}
                    data-rate={`${row.fromCurrency}-${row.toCurrency}-${row.effectiveOn}`}
                    className="border-t border-t-rule"
                  >
                    <td className="p-1 font-mono text-ink">{row.fromCurrency}</td>
                    <td className="p-1 font-mono text-ink">{row.toCurrency}</td>
                    <td className="p-1"><Num className="text-ink-meta">{row.effectiveOn}</Num></td>
                    <td className="p-1"><Num className="text-ink">{row.rate}</Num></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="flex flex-wrap items-end gap-2">
          <Field label={t('fx.rate.from')} data-rate-from value={from} onChange={(e) => setFrom(e.currentTarget.value.toUpperCase())} />
          <Field label={t('fx.rate.to')} data-rate-to value={to} onChange={(e) => setTo(e.currentTarget.value.toUpperCase())} />
          <Field label={t('fx.rate.on')} data-rate-on type="date" value={on} onChange={(e) => setOn(e.currentTarget.value)} />
          <Field label={t('fx.rate.value')} data-rate-value value={rate} onChange={(e) => setRate(e.currentTarget.value)} />
          <Button
            intent="primary"
            size="small"
            data-save-rate
            busy={saveRate.isPending}
            disabled={from.length !== 3 || to.length !== 3 || on === '' || rate.trim() === ''}
            onClick={() => saveRate.mutate()}
          >
            {t('fx.rate.save')}
          </Button>
        </div>
        {saveRate.isError ? (
          <p data-rate-refusal className="text-dense text-danger-text">
            {errorMessage(saveRate.error)}
          </p>
        ) : null}
      </div>
    </Panel>
  );
}
